
import { computed, onMounted, ref, watch } from 'vue';
import { cloneDeep } from 'lodash';
import { NodeType } from '@amplify/amp-module';
import RichTextEditor from '@/components/rte/RichTextEditor';
import NodeWrapper from '@/components/graph/common/NodeWrapper';
import { NodeSizes } from '@/constants/builder';
import { onlyDigitsRegEx } from '@/constants/regularExpressions';
import { isButtonMultiline } from '@/utils/builder-utils';
import { useNodeUtils } from '@/composables/useNodeUtils';
import { getInnerText } from '@/utils';
import ButtonSubNode from '@/components/graph/nodes/groups/button/ButtonSubNode.vue';

export default {
  components: { ButtonSubNode, RichTextEditor, NodeWrapper },

  setup() {
    const nodeType = NodeType.question;
    const subNodeType = NodeType.buttonGroup;

    const {
      ampData,
      uiData,
      nodeId,
      nodeRef,
      nodeSelf,
      selectedSubNodeIndex,
      subNodesArray,
      canEditAmp,
      addStorageOption,
      addSubNode,
      deleteStorage,
      onSelectSubNode,
      updateDelay,
    } = useNodeUtils({ nodeType });

    const maxStorageSize = NodeSizes[nodeType].width - 3;
    const nodeActions = [{ key: 'right', handler: addSubNode }];

    const messageHovered = ref(false);
    const messageUiData = ref({ hovered: false, selected: false });
    const htmlEditor = ref(null);

    const isDone = computed(() => getInnerText(ampData.value.html).length > 0);
    const buttonsAmpData = computed(() => ({ delay: ampData.value.buttonsDelay }));

    onMounted(() => {
      // select message sub node if node is selected
      if (uiData.value.selected && selectedSubNodeIndex.value === -1) {
        onSelectSubNodeCustom(-1, true);
        htmlEditor.value?.onEditorClick();
      }
    });

    const onEditorChange = ({ html }) => {
      if (!canEditAmp.value) {
        return;
      }

      ampData.value = { ...ampData.value, html };
    };

    const updateButtonsDelay = (delay) => {
      ampData.value = {
        ...ampData.value,
        buttonsDelayed: typeof delay === 'string' ? parseInt(delay.replace(onlyDigitsRegEx, '')) : delay,
      };
    };

    // same function is used in ButtonNode
    const onUpdateButtonSubNode = (data) => {
      const { id } = data;
      const subNodes = cloneDeep(ampData.value.subNodes);
      const hadMultilineContent = Object.values(subNodes || {}).some((subNode) =>
        isButtonMultiline(subNode.buttonTexts)
      );

      subNodes[id] = data;
      ampData.value = { ...ampData.value, subNodes };

      const hasMultilineContent = Object.values(subNodes || {}).some((subNode) =>
        isButtonMultiline(subNode.buttonTexts)
      );

      const { width, height } = nodeSelf.value.size();

      if (hadMultilineContent && !hasMultilineContent) {
        nodeSelf.value.resize(width, height - 28);
      }

      if (!hadMultilineContent && hasMultilineContent) {
        nodeSelf.value.resize(width, height + 28);
      }
    };

    const onSelectSubNodeCustom = (buttonIndex = -1, isMessageSelected = false) => {
      messageUiData.value = { ...messageUiData.value, selected: isMessageSelected };

      onSelectSubNode(buttonIndex);
    };

    watch(
      () => uiData.value.selected,
      (selected) => {
        if (!selected) {
          messageUiData.value = { ...messageUiData.value, selected: false };
        }
      }
    );

    watch(
      () => messageUiData.value.selected,
      (selected) => {
        const { width, height } = nodeSelf.value.size();
        // resize node if message sub node is selected or not
        nodeSelf.value.resize(width, selected ? height + 28 : height - 28);
      }
    );

    watch(
      () => uiData.value.hovered,
      (hovered) => {
        if (!hovered) {
          messageUiData.value = { ...messageUiData.value, hovered: false };
        }
      }
    );

    return {
      ampData,
      buttonsAmpData,
      canEditAmp,
      htmlEditor,
      isDone,
      maxStorageSize,
      messageHovered,
      messageUiData,
      nodeActions,
      nodeId,
      nodeRef,
      nodeSelf,
      nodeType,
      uiData,
      selectedSubNodeIndex,
      subNodeType,
      subNodesArray,
      addStorageOption,
      deleteStorage,
      updateButtonsDelay,
      updateDelay,
      onEditorChange,
      onSelectSubNode,
      onSelectSubNodeCustom,
      onUpdateButtonSubNode,
    };
  },
};
